import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FilterChip } from '../../crm/leads/components/FilterChip';
import { AttendanceFilters as IAttendanceFilters } from '../types';
import { FiltersButton } from '@/shared/components/ui';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
// ═══════════════════════════════════════════════════════
// FILTER DEFINITIONS
// ═══════════════════════════════════════════════════════

interface FilterOption {
  value: string;
  label: string;
  logo?: string;
}

interface FilterDefinition {
  key: keyof IAttendanceFilters;
  label: string;
  icon: IconName;
  placeholder: string;
}

const FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    key: 'channelId',
    label: 'Canal',
    icon: 'message-circle',
    placeholder: 'Buscar canal...',
  },
  {
    key: 'teamId',
    label: 'Equipe',
    icon: 'users',
    placeholder: 'Buscar equipe...',
  },
  {
    key: 'providerId',
    label: 'Provider',
    icon: 'server',
    placeholder: 'Buscar provider...',
  },
];

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

interface ActiveFilter {
  key: string;
  label: string;
  displayValue: string;
}

function getActiveFilters(
  filters: IAttendanceFilters,
  channels: FilterOption[],
  teams: FilterOption[],
  providers: FilterOption[]
): ActiveFilter[] {
  const entries: ActiveFilter[] = [];

  const lookupMap: Record<string, { label: string; options: FilterOption[] }> = {
    channelId: { label: 'Canal', options: channels },
    teamId: { label: 'Equipe', options: teams },
    providerId: { label: 'Provider', options: providers },
  };

  for (const [key, config] of Object.entries(lookupMap)) {
    const value = filters[key as keyof IAttendanceFilters];
    if (!value || typeof value !== 'string') continue;

    const option = config.options.find((o) => o.value === value);
    const displayValue = option?.label || value;

    entries.push({ key, label: config.label, displayValue });
  }

  return entries;
}

// ═══════════════════════════════════════════════════════
// PROPS
// ═══════════════════════════════════════════════════════

/**
 * Props do AttendanceFiltersChips
 */
export interface AttendanceFiltersProps {
  /** Filtros atuais */
  filters: IAttendanceFilters;
  /** Callback quando filtros mudam */
  onFiltersChange: (filters: IAttendanceFilters) => void;
  /** Canais disponveis para filtro */
  channels?: Array<{ id: string; name: string }>;
  /** Equipes disponveis para filtro */
  teams?: Array<{ id: string; name: string }>;
  /** Providers disponveis para filtro */
  providers?: Array<{ id: string; name: string; logo?: string }>;
}

// ═══════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════

/**
 * Filtros de atendimento com design chip-based (estilo Linear/Attio).
 *
 * Filtros ativos exibidos como chips clicáveis. Novos filtros
 * adicionados via popover com lista de filtros e seletor de valores.
 * Filtros são single-select e preenchidos via API (canais, equipes, providers).
 *
 * @example
 * ```tsx
 * <AttendanceFiltersComponent
 *   filters={filters}
 *   onFiltersChange={setFilters}
 *   channels={channels}
 *   teams={teams}
 *   providers={providers}
 * />
 * ```
 */
export const AttendanceFiltersComponent: React.FC<AttendanceFiltersProps & {
  /** 'toolbar' = só o botão `.btn` Filtros c/ ct-badge roxo · 'chips' = só fchips + Limpar tudo · 'full' = legado */
  variant?: 'full' | 'toolbar' | 'chips';
}> = ({
  filters,
  onFiltersChange,
  channels = [],
  teams = [],
  providers = [],
  variant = 'full',
}) => {
  // ── Popover state ──
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<'filters' | 'values'>('filters');
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [optionSearchQuery, setOptionSearchQuery] = useState('');

  // ── Positioning ──
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Âncora do variant 'toolbar' (FiltersButton shared não expõe ref — wrapper)
  const toolbarWrapRef = useRef<HTMLSpanElement>(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  // ── Transform props to FilterOption arrays ──
  const channelOptions: FilterOption[] = useMemo(
    () => channels.map((c) => ({ value: c.id, label: c.name })),
    [channels]
  );
  const teamOptions: FilterOption[] = useMemo(
    () => teams.map((t) => ({ value: t.id, label: t.name })),
    [teams]
  );
  const providerOptions: FilterOption[] = useMemo(
    () =>
      providers.map((p) => ({
        value: p.id,
        label: p.name,
        logo: p.logo,
      })),
    [providers]
  );

  const optionsMap: Record<string, FilterOption[]> = useMemo(
    () => ({
      channelId: channelOptions,
      teamId: teamOptions,
      providerId: providerOptions,
    }),
    [channelOptions, teamOptions, providerOptions]
  );

  // ── Derived state ──
  const activeFilters = useMemo(
    () => getActiveFilters(filters, channelOptions, teamOptions, providerOptions),
    [filters, channelOptions, teamOptions, providerOptions]
  );
  const hasFilters = activeFilters.length > 0;

  const currentFilterDef = selectedFilter
    ? FILTER_DEFINITIONS.find((d) => d.key === selectedFilter) || null
    : null;

  const currentOptions = selectedFilter ? optionsMap[selectedFilter] || [] : [];

  // ── Filtered options for search ──
  const filteredOptions = useMemo(() => {
    if (!optionSearchQuery.trim()) return currentOptions;
    const query = optionSearchQuery.toLowerCase();
    return currentOptions.filter((o) => o.label.toLowerCase().includes(query));
  }, [currentOptions, optionSearchQuery]);

  // ── Filtered filter list for search ──
  const filteredFilterDefs = useMemo(() => {
    if (!searchQuery.trim()) return FILTER_DEFINITIONS;
    const query = searchQuery.toLowerCase();
    return FILTER_DEFINITIONS.filter((d) => d.label.toLowerCase().includes(query));
  }, [searchQuery]);

  // ── Update popover position ──
  useEffect(() => {
    const anchor = toolbarWrapRef.current ?? triggerRef.current;
    if (isOpen && anchor) {
      const rect = anchor.getBoundingClientRect();
      // Clamp na viewport (painel w-72 = 288px) — no mobile o trigger fica na
      // borda direita e o popover vazava pra fora da tela
      const PANEL_W = 288;
      setPopoverPosition({
        top: rect.bottom + window.scrollY + 4,
        left: Math.max(8, Math.min(rect.left + window.scrollX, window.innerWidth - PANEL_W - 8)),
      });
    }
  }, [isOpen]);

  // ── Open popover: add new filter ──
  const handleOpenAdd = () => {
    setStep('filters');
    setSelectedFilter(null);
    setSearchQuery('');
    setOptionSearchQuery('');
    setIsOpen(true);
  };

  // ── Open popover: edit existing chip ──
  const handleEditChip = (filterKey: string) => {
    setStep('values');
    setSelectedFilter(filterKey);
    setOptionSearchQuery('');
    setIsOpen(true);
  };

  // ── Select filter from list ──
  const handleSelectFilter = (filterKey: string) => {
    setSelectedFilter(filterKey);
    setOptionSearchQuery('');
    setStep('values');
  };

  // ── Select a value (single-select) ──
  const handleSelectValue = (value: string) => {
    if (!selectedFilter) return;

    const currentValue = filters[selectedFilter as keyof IAttendanceFilters];
    const newFilters = { ...filters };

    if (currentValue === value) {
      // Deselect if same value
      delete newFilters[selectedFilter as keyof IAttendanceFilters];
    } else {
      (newFilters as Record<string, unknown>)[selectedFilter] = value;
    }

    onFiltersChange(newFilters);
    setIsOpen(false);
  };

  // ── Remove a chip ──
  const handleRemoveChip = (filterKey: string) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey as keyof IAttendanceFilters];
    onFiltersChange(newFilters);
  };

  // ── Clear all filters ──
  const handleClearAll = () => {
    onFiltersChange({});
  };

  // ── Close popover ──
  const handleClose = () => {
    setIsOpen(false);
  };

  // ── Back to filter list ──
  const handleBack = () => {
    setStep('filters');
    setSelectedFilter(null);
    setSearchQuery('');
  };

  // ── Check if a filter is active ──
  const isFilterActive = (filterKey: string): boolean => {
    const value = filters[filterKey as keyof IAttendanceFilters];
    return value !== undefined && value !== null && value !== '';
  };

  // Popover (portal) — compartilhado pelas variants toolbar/chips/full
  const renderPopover = () => (
    <>
      {isOpen &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-[100]"
              onClick={handleClose}
            />

            {/* Panel */}
            <div
              className="fixed z-[101] w-72 rounded-lg shadow-xl flex flex-col"
              style={{
                top: `${popoverPosition.top}px`,
                left: `${popoverPosition.left}px`,
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                maxHeight: '380px',
              }}
              data-testid="attendance-filters-popover"
            >
              {step === 'filters' ? (
                /* ═══════════════════════════════════════ */
                /* STEP 1: FILTER LIST                    */
                /* ═══════════════════════════════════════ */
                <>
                  {/* Search */}
                  <div
                    className="p-3 flex-shrink-0"
                    style={{ borderBottom: '1px solid #e5e7eb' }}
                  >
                    <div className="relative">
                      <Icon name="search" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
                        style={{ color: '#9ca3af' }} />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Buscar filtro..."
                        className="w-full h-[38px] pl-8 pr-3 py-1.5 text-sm rounded-md border"
                        style={{
                          backgroundColor: '#f9fafb',
                          borderColor: '#e5e7eb',
                          color: '#1e293b',
                        }}
                        autoFocus
                        data-testid="attendance-filters-search"
                      />
                    </div>
                  </div>

                  {/* Filter List */}
                  <div className="overflow-y-auto flex-1 py-1">
                    {filteredFilterDefs.length === 0 ? (
                      <div
                        className="text-center py-6 text-sm"
                        style={{ color: '#9ca3af' }}
                      >
                        Nenhum filtro encontrado
                      </div>
                    ) : (
                      filteredFilterDefs.map((def) => {
                        const active = isFilterActive(def.key);

                        return (
                          <button
                            key={def.key}
                            type="button"
                            onClick={() => handleSelectFilter(def.key)}
                            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors text-left"
                            style={{ color: '#1e293b' }}
                            data-testid={`attendance-filter-item-${def.key}`}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f9fafb';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <Icon
                              name={def.icon}
                              className="w-4 h-4 flex-shrink-0"
                              style={{ color: '#9ca3af' }}
                            />
                            <span className="flex-1">{def.label}</span>
                            {active && (
                              <div
                                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: '#8b5cf6' }}
                              />
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </>
              ) : (
                /* ═══════════════════════════════════════ */
                /* STEP 2: VALUE SELECTOR                 */
                /* ═══════════════════════════════════════ */
                <>
                  {/* Header with back button */}
                  <div
                    className="flex items-center gap-2 px-3 py-2.5 flex-shrink-0"
                    style={{ borderBottom: '1px solid #e5e7eb' }}
                  >
                    <button
                      type="button"
                      onClick={handleBack}
                      className="p-1 rounded transition-colors"
                      style={{ color: '#64748b' }}
                      data-testid="attendance-filters-back"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f1f5f9';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <Icon name="chevron-left" className="w-4 h-4" />
                    </button>
                    <span
                      className="text-sm font-semibold"
                      style={{ color: '#1e293b' }}
                    >
                      {currentFilterDef?.label}
                    </span>
                  </div>

                  {/* Search (only if many options) */}
                  {currentOptions.length > 5 && (
                    <div className="px-3 pt-3 pb-1">
                      <div className="relative">
                        <Icon name="search" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
                          style={{ color: '#9ca3af' }} />
                        <input
                          type="text"
                          value={optionSearchQuery}
                          onChange={(e) => setOptionSearchQuery(e.target.value)}
                          placeholder={currentFilterDef?.placeholder || 'Buscar...'}
                          className="w-full h-[38px] pl-8 pr-3 py-1.5 text-sm rounded-md border"
                          style={{
                            backgroundColor: '#f9fafb',
                            borderColor: '#e5e7eb',
                            color: '#1e293b',
                          }}
                          autoFocus
                          data-testid="attendance-filters-option-search"
                        />
                      </div>
                    </div>
                  )}

                  {/* Options */}
                  <div className="overflow-y-auto flex-1 p-2 space-y-0.5">
                    {filteredOptions.length === 0 ? (
                      <div
                        className="text-center py-4 text-sm"
                        style={{ color: '#9ca3af' }}
                      >
                        Nenhuma opção encontrada
                      </div>
                    ) : (
                      filteredOptions.map((option) => {
                        const isSelected =
                          selectedFilter != null &&
                          filters[selectedFilter as keyof IAttendanceFilters] === option.value;

                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => handleSelectValue(option.value)}
                            data-testid={`attendance-filter-option-${option.value}`}
                            className="w-full flex items-center justify-between px-3 py-2 rounded text-sm transition-colors text-left"
                            style={{
                              backgroundColor: isSelected ? '#f5f3ff' : 'transparent',
                              color: '#1e293b',
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected)
                                e.currentTarget.style.backgroundColor = '#f9fafb';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = isSelected
                                ? '#f5f3ff'
                                : 'transparent';
                            }}
                          >
                            <span>{option.label}</span>
                            {isSelected && (
                              <div
                                className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: '#8b5cf6' }}
                              >
                                <Icon name="check" className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </div>
          </>,
          document.body
        )}
    </>
  );

  if (variant === 'toolbar') {
    return (
      <>
        {/* F05: botão Filtros canônico (FiltersButton shared, 38px, funil 16px,
            badge roxo inline). Legenda colapsa no padrão do DS 2026-08-04
            (hidden 2xl:@tb:inline, embutido no shared); wrapper span só ancora
            o popover. */}
        <span ref={toolbarWrapRef} className="flex-none inline-flex">
          <FiltersButton
            count={activeFilters.length}
            active={isOpen}
            onClick={handleOpenAdd}
            data-testid="attendance-filters-button"
          />
        </span>
        {renderPopover()}
      </>
    );
  }

  if (variant === 'chips') {
    if (!hasFilters) return null;
    return (
      <>
        {activeFilters.map((filter) => (
          /* `.fchip` do mockup: 26px lilás, "Label: Valor" + × 18px */
          <span
            key={filter.key}
            className="inline-flex items-center gap-1 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap flex-none"
            style={{
              height: 26,
              padding: '0 5px 0 11px',
              backgroundColor: 'var(--theme-primary-background)',
              color: 'var(--theme-primary)',
            }}
            onClick={() => handleEditChip(filter.key)}
            data-testid={`attendance-filter-chip-${filter.key}`}
          >
            {filter.label}: {filter.displayValue}
            <button
              className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 transition-opacity hover:opacity-100 hover:bg-black/10"
              onClick={(e) => { e.stopPropagation(); handleRemoveChip(filter.key); }}
              aria-label="remover filtro"
            >
              <Icon name="x" className="w-3 h-3" />
            </button>
          </span>
        ))}
        <button
          onClick={handleClearAll}
          className="text-xs font-semibold transition-colors flex-none"
          style={{ color: 'var(--theme-muted-text)' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--theme-text)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--theme-muted-text)'; }}
          data-testid="attendance-filters-clear"
        >
          Limpar tudo
        </button>
        {renderPopover()}
      </>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* ═══ Active Filter Chips ═══ */}
      {activeFilters.map((filter) => (
        <FilterChip
          key={filter.key}
          label={filter.label}
          value={filter.displayValue}
          onClick={() => handleEditChip(filter.key)}
          onRemove={() => handleRemoveChip(filter.key)}
        />
      ))}

      {/* ═══ Add Filter Button ═══ */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleOpenAdd}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200"
        style={{
          backgroundColor: 'var(--theme-button-ghost)',
          color: 'var(--theme-header-text)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--theme-header-button-hover)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost)';
        }}
      >
        <Icon name="plus" className="w-3.5 h-3.5" />
        <span>Filtro</span>
      </button>

      {/* ═══ Clear All Button ═══ */}
      {hasFilters && (
        <button
          type="button"
          onClick={handleClearAll}
          className="text-xs font-medium px-2 py-1.5 rounded transition-colors"
          style={{ color: 'var(--theme-muted-text)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--theme-header-text)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--theme-muted-text)';
          }}
        >
          Limpar
        </button>
      )}

      {renderPopover()}
    </div>
  );
};

// Keep backward-compatible export name
export const AttendanceFilters = AttendanceFiltersComponent;

AttendanceFiltersComponent.displayName = 'AttendanceFiltersComponent';
